import {
  PRODUCT_IMAGE_SIZE_DESKTOP,
  PRODUCT_IMAGE_SIZE_MEDIUM,
  PRODUCT_IMAGE_SIZE_MOBILE,
  PRODUCT_IMAGE_SIZE_THUMBNAIL,
} from '@/feature/product/services/product.constants'
import type { UseTranslationReturnType } from '@/lib/i18n/types'
import type { CloudflarePictureProductSizes } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { PICTURE_PLACEHOLDER_IMAGE_URL } from '@/ui/media/picture/Picture.client'
import {
  IFRAME_VIDEO_CLASSNAME,
  VIMEO_HOSTNAME,
  VIMEO_IFRAME_HOSTNAME,
  VIMEO_PLATFORM,
  VIMEO_VIDEO_ID_REGEX,
  VIMEO_VIDEO_PATH,
  YOUTUBE_EMBED_PATH,
  YOUTUBE_EMBED_VIDEO_ID_REGEX,
  YOUTUBE_HOSTNAME,
  YOUTUBE_IFRAME_HOSTNAME,
  YOUTUBE_PLATFORM,
  YOUTUBE_SHORT_HOSTNAME,
} from './video.constants'

export { aspectVariants } from './video.constants'
export const IFRAME_REGEX = /<iframe[^>]*>(?:[\s\S]*?<\/iframe>)?/gi
export const IFRAME_SRC_REGEX = /src=["']([^"']+)["']/i
export const YOUTUBE_EMBED_PARAMS = new URLSearchParams({ rel: '0' })
export const VIMEO_EMBED_PARAMS = new URLSearchParams({ byline: '0', portrait: '0' })
export const VIMEO_SHARE_REGEX = new RegExp(`${VIMEO_HOSTNAME.replace('.', '\\.')}\\/([0-9]+)`)

const VIDEO_PROVIDERS: Record<string, VideoProvider> = {
  [YOUTUBE_IFRAME_HOSTNAME]: {
    platform: YOUTUBE_PLATFORM,
    regex: YOUTUBE_EMBED_VIDEO_ID_REGEX,
  },
  [VIMEO_IFRAME_HOSTNAME]: {
    platform: VIMEO_PLATFORM,
    regex: VIMEO_VIDEO_ID_REGEX,
  },
}

/** Uses CloudflarePictureProductSizes (80, 140, 270, 600) or `maxres` for maximum resolution */
export type ThumbnailSize = CloudflarePictureProductSizes | 'maxres'

export type VideoInfo = {
  platform: 'youtube' | 'vimeo' | null
  videoId: string | null
}

type VideoProvider = {
  platform: NonNullable<VideoInfo['platform']>
  regex: RegExp
}

/** Extracts platform and video ID from an iframe embed src (YouTube or Vimeo) */
export function extractVideoInfo(src: string): VideoInfo {
  try {
    const url = new URL(src)
    const provider = VIDEO_PROVIDERS[url.hostname]

    if (!provider) {
      return { platform: null, videoId: null }
    }

    const match = url.pathname.match(provider.regex)

    return {
      platform: provider.platform,
      videoId: match?.[1] ?? null,
    }
  } catch {
    return { platform: null, videoId: null }
  }
}

/** Extracts a YouTube video ID from watch, short or embed URL formats */
function extractYoutubeVideoId(url: string): string | null {
  const embedPrefix = `${YOUTUBE_IFRAME_HOSTNAME}/${YOUTUBE_EMBED_PATH}/`

  if (url.includes(`${YOUTUBE_HOSTNAME}/watch`)) {
    return new URL(url).searchParams.get('v')
  }

  if (url.includes(`${YOUTUBE_SHORT_HOSTNAME}/`)) {
    return url.split(`${YOUTUBE_SHORT_HOSTNAME}/`)[1]?.split('?')[0] || null
  }

  if (url.includes(embedPrefix)) {
    return url.split(embedPrefix)[1]?.split('?')[0] || null
  }

  return null
}

/** Extracts a Vimeo video ID from player embed or share URL formats */
function extractVimeoVideoId(url: string): string | null {
  const videoPrefix = `${VIMEO_IFRAME_HOSTNAME}/${VIMEO_VIDEO_PATH}/`

  if (url.includes(videoPrefix)) {
    return url.split(videoPrefix)[1]?.split('?')[0] || null
  }

  if (url.includes(VIMEO_HOSTNAME)) {
    const vimeoRegex = new RegExp(`${VIMEO_HOSTNAME.replace('.', '\\.')}\\/(\\d+)`)
    const match = url.match(vimeoRegex)

    return match?.[1] || null
  }

  return null
}

/** Builds a YouTube watch URL from a video ID */
export function buildYoutubeWatchUrl(videoId: string): string {
  return `https://${YOUTUBE_IFRAME_HOSTNAME}/watch?v=${videoId}`
}

/** Builds a Vimeo share URL from a video ID */
export function buildVimeoShareUrl(videoId: string): string {
  return `https://${VIMEO_HOSTNAME}/${videoId}`
}

/** Builds a YouTube embed URL from a video ID, with optional query params */
export function buildYoutubeEmbedUrl(videoId: string, params?: URLSearchParams): string {
  const qs = params ? `?${params.toString()}` : ''

  return `https://${YOUTUBE_IFRAME_HOSTNAME}/${YOUTUBE_EMBED_PATH}/${videoId}${qs}`
}

/** Builds a Vimeo embed URL from a video ID, with optional query params */
export function buildVimeoEmbedUrl(videoId: string, params?: URLSearchParams): string {
  const qs = params ? `?${params.toString()}` : ''

  return `https://${VIMEO_IFRAME_HOSTNAME}/${VIMEO_VIDEO_PATH}/${videoId}${qs}`
}

/** Returns the YouTube thumbnail URL for a given video ID and size */
export function getYoutubeThumbnailUrl(videoId: string, size: ThumbnailSize = 'maxres'): string {
  const sizeMap: Record<ThumbnailSize, string> = {
    [PRODUCT_IMAGE_SIZE_THUMBNAIL]: 'default', // YouTube: 120x90
    [PRODUCT_IMAGE_SIZE_MEDIUM]: 'mqdefault', // YouTube: 320x180
    [PRODUCT_IMAGE_SIZE_MOBILE]: 'hqdefault', // YouTube: 480x360
    [PRODUCT_IMAGE_SIZE_DESKTOP]: 'sddefault', // YouTube: 640x480
    maxres: 'maxresdefault', // YouTube: 1280x720
  }

  const quality = sizeMap[size]

  return `https://img.${YOUTUBE_HOSTNAME}/vi/${videoId}/${quality}.jpg`
}

/** Fetches the Vimeo thumbnail URL via the official oEmbed API, falls back to placeholder */
export async function fetchVimeoThumbnailUrl(videoId: string): Promise<string> {
  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=https://${VIMEO_HOSTNAME}/${videoId}`,
    )

    if (!response.ok) {
      return PICTURE_PLACEHOLDER_IMAGE_URL
    }

    const data = await response.json()

    return data.thumbnail_url ?? PICTURE_PLACEHOLDER_IMAGE_URL
  } catch (error) {
    console.error('Error fetching Vimeo thumbnail:', error)

    return PICTURE_PLACEHOLDER_IMAGE_URL
  }
}

/** Fetches the thumbnail URL for a YouTube or Vimeo video URL, falls back to placeholder */
export async function fetchVideoThumbnailUrl(
  videoUrl: string,
  size: ThumbnailSize = 'maxres',
): Promise<string> {
  if (videoUrl.includes(VIMEO_HOSTNAME) || videoUrl.includes(VIMEO_IFRAME_HOSTNAME)) {
    const videoId = extractVimeoVideoId(videoUrl)

    return videoId ? fetchVimeoThumbnailUrl(videoId) : PICTURE_PLACEHOLDER_IMAGE_URL
  }

  if (videoUrl.includes(YOUTUBE_HOSTNAME) || videoUrl.includes(YOUTUBE_SHORT_HOSTNAME)) {
    const videoId = extractYoutubeVideoId(videoUrl)

    return videoId ? getYoutubeThumbnailUrl(videoId, size) : PICTURE_PLACEHOLDER_IMAGE_URL
  }

  return PICTURE_PLACEHOLDER_IMAGE_URL
}

/** Builds a YouTube embed URL from any supported src format (embed, watch, short) */
export function buildYoutubeEmbedUrlFromSrc(src: string): string | null {
  if (src.includes(YOUTUBE_IFRAME_HOSTNAME) && src.includes('/embed/')) {
    const videoId = src.match(YOUTUBE_EMBED_VIDEO_ID_REGEX)?.[1]

    return videoId ? buildYoutubeEmbedUrl(videoId, YOUTUBE_EMBED_PARAMS) : null
  }

  if (src.includes(`${YOUTUBE_HOSTNAME}/watch`)) {
    try {
      const videoId = new URL(src).searchParams.get('v')

      return videoId ? buildYoutubeEmbedUrl(videoId, YOUTUBE_EMBED_PARAMS) : null
    } catch {
      return null
    }
  }

  if (src.includes(`${YOUTUBE_SHORT_HOSTNAME}/`)) {
    const videoId = src.split(`${YOUTUBE_SHORT_HOSTNAME}/`)[1]?.split('?')[0]

    return videoId ? buildYoutubeEmbedUrl(videoId, YOUTUBE_EMBED_PARAMS) : null
  }

  return null
}

/** Builds a Vimeo embed URL from any supported src format (player, share) */
export function buildVimeoEmbedUrlFromSrc(src: string): string | null {
  if (src.includes(VIMEO_IFRAME_HOSTNAME)) {
    const videoId = src.match(VIMEO_VIDEO_ID_REGEX)?.[1]

    return videoId ? buildVimeoEmbedUrl(videoId, VIMEO_EMBED_PARAMS) : null
  }

  if (src.includes(VIMEO_HOSTNAME)) {
    const videoId = src.match(VIMEO_SHARE_REGEX)?.[1]

    return videoId ? buildVimeoEmbedUrl(videoId, VIMEO_EMBED_PARAMS) : null
  }

  return null
}

/** Builds an embed URL from any YouTube or Vimeo src format, returns null if unrecognized */
export function buildEmbedUrl(src: string): string | null {
  return buildYoutubeEmbedUrlFromSrc(src) ?? buildVimeoEmbedUrlFromSrc(src)
}

/**
 * Creates an iframe element based on iframe src
 * Returns a styled iframe for YouTube/Vimeo or the original iframe HTML
 */
export function createVideoElement(
  iframe: { match: string; src: string; index: number },
  descriptionClassName: string,
  t: UseTranslationReturnType<'generic'>['t'],
): React.ReactElement {
  const videoInfo = extractVideoInfo(iframe.src)

  if (videoInfo.platform === 'youtube' && videoInfo.videoId) {
    return (
      <iframe
        key={`youtube-${videoInfo.videoId}-${iframe.index}`}
        className={IFRAME_VIDEO_CLASSNAME}
        src={buildYoutubeEmbedUrl(videoInfo.videoId)}
        title={t('generic:media.youtubeVideo')}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    )
  }

  if (videoInfo.platform === 'vimeo' && videoInfo.videoId) {
    return (
      <iframe
        key={`vimeo-${videoInfo.videoId}-${iframe.index}`}
        className={IFRAME_VIDEO_CLASSNAME}
        src={buildVimeoEmbedUrl(videoInfo.videoId)}
        title={t('generic:media.vimeoVideo')}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    )
  }

  // Keep non-YouTube/Vimeo iframes as is
  return (
    <div
      key={`iframe-${iframe.index}`}
      className={descriptionClassName}
      dangerouslySetInnerHTML={{ __html: iframe.match }}
    />
  )
}

/**
 * Parses HTML and replaces YouTube/Vimeo iframes with React video components
 * Returns array of React elements mixing HTML fragments and video components
 * Returns null if no iframes are found (to use default rendering)
 */
export function parseAndReplaceIframes(
  html: string,
  descriptionClassName: string,
  t: UseTranslationReturnType<'generic'>['t'],
): React.ReactElement[] | null {
  const iframes: Array<{ match: string; src: string; index: number }> = []
  let match: RegExpExecArray | null

  // Reset regex state
  IFRAME_REGEX.lastIndex = 0

  // Find all iframes and their src attributes
  while ((match = IFRAME_REGEX.exec(html)) !== null) {
    const srcMatch = match[0].match(IFRAME_SRC_REGEX)

    if (srcMatch && srcMatch[1]) {
      iframes.push({
        match: match[0],
        src: srcMatch[1],
        index: match.index,
      })
    }
  }

  // If no iframes found, return null to use default rendering
  if (iframes.length === 0) {
    return null
  }

  const elements: React.ReactElement[] = []
  let lastIndex = 0

  for (const iframe of iframes) {
    // Add HTML before this iframe
    if (iframe.index > lastIndex) {
      const htmlFragment = html.substring(lastIndex, iframe.index)

      if (htmlFragment.trim()) {
        elements.push(
          <div
            key={`html-${iframe.index}`}
            className={descriptionClassName}
            dangerouslySetInnerHTML={{ __html: htmlFragment }}
          />,
        )
      }
    }

    // Add video component or original iframe
    elements.push(createVideoElement(iframe, descriptionClassName, t))

    lastIndex = iframe.index + iframe.match.length
  }

  // Add remaining HTML after the last iframe
  if (lastIndex < html.length) {
    const htmlFragment = html.substring(lastIndex)

    if (htmlFragment.trim()) {
      elements.push(
        <div
          key="html-after"
          className={descriptionClassName}
          dangerouslySetInnerHTML={{ __html: htmlFragment }}
        />,
      )
    }
  }

  return elements
}
